import React, { useState } from 'react';
import {
  Button,
  Chip,
  CircularProgress,
  Grid,
  makeStyles,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { parseJd, parseResume } from 'controllers/talentAiMatching/uploadFiles';
import { rankTalentForExplainableMatch } from 'controllers/talentAiMatching/rankTalent';
import { setFlag } from 'controllers/flags';
import { ParsedResume } from './parsedResume';

const useStyles = makeStyles(() => ({
  form: {
    padding: 24,
  },
  formSection: {
    background: '#fff',
    borderRadius: 2,
    marginTop: 24,
  },
  dropZoneContainer: {
    height: 120,
    width: '100%',
    border: '1px dashed #4b8c6f',
  },
  title: {
    opacity: 0.6,
    fontSize: '16px',
    fontWeight: 'normal',
    lineHeight: 1.31,
    letterSpacing: '0.16px',
    color: '#000000',
    marginBottom: 21,
  },
  btnsContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: 24,
  },
  btns: {
    width: 144,
    marginRight: 10,
  },
  progress: {
    marginLeft: 8,
  },
  chip: {
    maxWidth: 130,
    marginRight: 7,
  },
  buttonTab: {
    marginRight: 8,
    fontFamily: 'Roboto',
    fontSize: 14,
    fontWeight: 'normal',
    fontStyle: 'normal',
    letterSpacing: 0.25,
  },
}));

const ExplainableMatchForm = ({
  setTalentList,
  talentList = [],
  updateCredits,
}) => {
  const classes = useStyles();
  const [jdList, setJdList] = useState([]);
  const [resumeList, setResumeList] = useState({});
  const [isJdUploading, setIsJdUploading] = useState(false);
  const [isRanking, setIsRanking] = useState(false);
  const [skillSearch, setSkillSearch] = useState('');
  const [minExperience, setMinExperience] = useState(null);
  const [maxExperience, setMaxExperience] = useState(null);
  const [description, setDescription] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [skills, setSkills] = useState([]);
  const [showResumeOverview, setShowResumeOverview] = useState(false);

  const handleDrop = (func, key) => async (files = [], errorFiles = []) => {
    if (errorFiles.length) {
      setFlag('TalentAiFileTypeError', true);
    }

    if (files.length && key === 'Jd') {
      setIsJdUploading(true);

      const response = await func(files);
      const {
        job_title = '',
        minimum_required_experience = null,
        maximum_required_experience = null,
        raw_text = '',
        skills = [],
      } = response && response.content ? response.content : {};

      setJdList([response]);
      setMinExperience(Number(minimum_required_experience) || null);
      setMaxExperience(Number(maximum_required_experience) || null);
      setDescription(raw_text);
      setSkills(skills);
      setJobTitle(job_title);
      setIsJdUploading(false);
    } else if (files.length && key === 'Resume') {
      setResumeList((prevState) => ({ ...prevState, [files[0].name]: null }));

      const response = await func(files);

      setResumeList((prevState) => ({
        ...prevState,
        [files[0].name]: response,
      }));
    } else {
      return false;
    }
  };

  const onReset = () => {
    setJdList([]);
    setResumeList({});
    setTalentList([]);
    setMinExperience(null);
    setMaxExperience(null);
    setDescription('');
    setJobTitle('');
    setSkills([]);
  };

  const onRank = async () => {
    const resumes = Object.keys(resumeList);

    setIsRanking(true);
    const promiseList = resumes.map(async (resume) => {
      const result = await rankTalentForExplainableMatch(
        jdList,
        {
          jobTitle,
          minExperience,
          maxExperience,
          description,
          skills,
        },
        resumeList[resume]
      );

      return {
        resume,
        ...result,
      };
    });

    const list = await Promise.all(promiseList);

    setTalentList(list);
    setIsRanking(false);
    updateCredits();
  };

  const onDeleteJd = () => {
    setJdList([]);
  };

  const onDeleteResume = (deletedResumeName) => () => {
    setResumeList((prevState) => {
      const { [deletedResumeName]: deletedResume, ...otherResumes } = prevState;

      return otherResumes;
    });
  };

  const onSelectSkill = (event) => {
    if (event.key === 'Enter') {
      setSkills((prevSkills) => [...new Set([skillSearch, ...prevSkills])]);
      setSkillSearch('');
    }
  };

  const onDeleteSkill = (skill) => () => {
    setSkills((prevSkills) => prevSkills.filter((pSkill) => skill !== pSkill));
  };

  return (
    <>
      <div className={classes.formSection}>
        <div className={classes.form}>
          <Grid container>
            <Grid item xs={6}>
              <Typography classes={{ root: classes.title }}>
                Upload Job Description <br />
                (Accepts doc, docx, pdf, txt and zip)
              </Typography>
            </Grid>
            <Grid
              item
              xs={6}
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'flex-start',
              }}
            >
              {jdList.length ? (
                jdList.map(({ filename }) => (
                  <Chip
                    classes={{ root: classes.chip }}
                    title={filename}
                    key={filename}
                    label={filename}
                    onDelete={onDeleteJd}
                    color="default"
                    variant="outlined"
                  />
                ))
              ) : (
                <>
                  <input
                    // accept="pdf"
                    style={{ display: 'none' }}
                    id="jd-button"
                    single
                    type="file"
                    onChange={(event) => {
                      handleDrop(parseJd, 'Jd')(Array.from(event.target.files));
                    }}
                  />
                  {isJdUploading ? (
                    <CircularProgress
                      size={16}
                      color="primary"
                      classes={{ root: classes.progress }}
                    />
                  ) : (
                    <label htmlFor="jd-button">
                      <Button variant={'text'} color="primary" component="span">
                        + UPLOAD JD
                      </Button>
                    </label>
                  )}
                </>
              )}
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                id="job-title"
                label="Job Title"
                variant="outlined"
                fullWidth
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                id="add-skill"
                label="Add Skills"
                variant="outlined"
                fullWidth
                value={skillSearch}
                onChange={(e) => setSkillSearch(e.target.value)}
                onKeyPress={onSelectSkill}
              />
            </Grid>

            <Grid item xs={12}>
              {skills.slice(0, 4).map((skill) => (
                <Chip
                  classes={{ root: classes.chip }}
                  title={skill}
                  key={skill}
                  label={skill}
                  onDelete={onDeleteSkill(skill)}
                  color="default"
                  variant="outlined"
                />
              ))}
              {skills.length > 4 ? (
                <Tooltip title={skills.slice(4).join(', ')}>
                  <Chip
                    classes={{ root: classes.chip }}
                    label={<>{`+ ${skills.length - 4} More`}</>}
                    color="default"
                    variant="outlined"
                  />
                </Tooltip>
              ) : null}
            </Grid>

            <Grid item xs={6}>
              <TextField
                id="add-skill"
                label="Min. Experience"
                variant="outlined"
                fullWidth
                value={minExperience}
                onChange={(e) => setMinExperience(e.target.value)}
                type="number"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                id="add-skill"
                label="Max. Experience"
                variant="outlined"
                fullWidth
                value={maxExperience}
                onChange={(e) => setMaxExperience(e.target.value)}
                type="number"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                id="add-skill"
                label=""
                variant="outlined"
                fullWidth
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                multiline
                rows={4}
              />
            </Grid>
          </Grid>
        </div>
        <div className={classes.form}>
          <Typography classes={{ root: classes.title }}>
            Upload Max. of 5 Resumes (Accepts doc, docx, pdf, png, jpeg & jpg)
          </Typography>
          {Object.keys(resumeList).map((resumeName) => (
            <>
              <Chip
                classes={{ root: classes.chip }}
                title={resumeName}
                key={resumeName}
                label={resumeName}
                onClick={() => setShowResumeOverview(resumeName)}
                icon={
                  resumeList[resumeName] !== null ? (
                    false
                  ) : (
                    <CircularProgress
                      size={16}
                      color="primary"
                      classes={{ root: classes.progress }}
                    />
                  )
                }
                onDelete={
                  resumeList[resumeName] !== null
                    ? onDeleteResume(resumeName)
                    : false
                }
                color="default"
                variant="outlined"
              />
              <ParsedResume
                open={
                  showResumeOverview === resumeName && resumeList[resumeName]
                }
                handleClose={() => setShowResumeOverview(false)}
                resume={resumeList[resumeName]}
              />
            </>
          ))}
          {Object.keys(resumeList).length < 5 ? (
            <>
              <br />
              <input
                // accept="pdf"
                style={{ display: 'none' }}
                id="resume-button"
                single
                type="file"
                onChange={(event) => {
                  handleDrop(
                    parseResume,
                    'Resume'
                  )(Array.from(event.target.files));
                }}
              />
              <label htmlFor="resume-button">
                <Button variant={'text'} color="primary" component="span">
                  + UPLOAD{Object.keys(resumeList).length ? ' MORE' : ''} RESUME
                </Button>
              </label>
            </>
          ) : null}
        </div>
      </div>
      <div className={classes.btnsContainer}>
        <Button
          variant="outlined"
          color="default"
          disabled={
            !jdList.length &&
            !Object.keys(resumeList).length &&
            !talentList.length
          }
          classes={{ root: classes.btns }}
          onClick={onReset}
        >
          Reset
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={
            !jdList.length ||
            !Object.keys(resumeList).every(
              (resume) => resumeList[resume] !== null
            ) ||
            isRanking
          }
          classes={{ root: classes.btns }}
          onClick={onRank}
        >
          Rank
          {isRanking ? (
            <CircularProgress
              size={16}
              color="primary"
              classes={{ root: classes.progress }}
            />
          ) : null}
        </Button>
      </div>
    </>
  );
};

export default ExplainableMatchForm;
